// Shared client-workspace navigation model. Single source of truth for the workspace's
// tabs + the guided 5-stage lifecycle, consumed by both the context-aware sidebar
// (WorkspaceNavPanel) and the WorkspaceDetail page so they never drift.
import {
  IdentificationCard, Gauge, Sparkle, ListChecks, Target, Compass, ShieldCheck, ClipboardText,
  Robot, Detective, Kanban, Certificate, Heartbeat, FlagBannerFold, SquaresFour, Warning,
  FileMagnifyingGlass, Scroll, SealCheck, ChatCircleText, Signature, CurrencyDollar, Stack,
  Briefcase, FolderOpen, Brain, BookOpen, FileText, ClockCounterClockwise, UsersThree, Tray,
  PlugsConnected, Broadcast, Gear, Pulse, TreeStructure, Binoculars, ListMagnifyingGlass,
} from "@phosphor-icons/react";

// Tab metadata. Canonical pills shown in the firm sub-nav PLUS backward-compat ids
// (knowledge/content/documents/review/history/audit/services) that deep-links still resolve.
export const TABS = [
  { v: "overview", label: "Overview", icon: IdentificationCard },
  { v: "posture", label: "Posture & Value", icon: Gauge },
  { v: "discovery", label: "Discovery", icon: Binoculars },
  { v: "live-assurance", label: "Live Assurance", icon: Broadcast },
  // Legacy-alias entries below (intelligence/insight360/security/connected-apps/assurance/
  // requirements/scoping/engagement-plan/sign-offs/compliance-program…) are kept ONLY for
  // TAB_MAP lookups + deep-link resolution — do NOT add them back to LIFECYCLE_STAGES ids.
  { v: "intelligence", label: "Intelligence", icon: Sparkle },
  { v: "insight360", label: "360° Insights", icon: Binoculars },
  { v: "ask", label: "Ask Tukoro", icon: ListMagnifyingGlass },
  { v: "requirements", label: "Requirements", icon: ListChecks },
  { v: "engagement", label: "Engagement Scope", icon: Target },
  { v: "scoping", label: "Scoping", icon: Compass },
  { v: "compliance", label: "Compliance Mapping", icon: ShieldCheck },
  { v: "assessments", label: "Assessments", icon: ClipboardText },
  { v: "readiness", label: "Framework Readiness", icon: TreeStructure },
  { v: "agents", label: "AI Agents", icon: Robot },
  { v: "understanding", label: "Client Understanding", icon: Detective },
  { v: "engagement-plan", label: "Engagement Plan", icon: Kanban },
  { v: "compliance-program", label: "Compliance Program", icon: Certificate },
  { v: "client-success", label: "Client Success", icon: Heartbeat },
  { v: "program", label: "Program & Plan", icon: FlagBannerFold },
  { v: "grc-dashboard", label: "GRC Dashboard", icon: SquaresFour },
  { v: "risks", label: "Risk Register", icon: Warning },
  { v: "tasks", label: "Remediation", icon: ListChecks },
  { v: "evidence", label: "Evidence", icon: FileMagnifyingGlass },
  { v: "policies", label: "Policies", icon: Scroll },
  { v: "audits", label: "Audits", icon: SealCheck },
  { v: "audit-requests", label: "Audit Requests", icon: ChatCircleText },
  { v: "sign-offs", label: "Sign-offs", icon: Signature },
  { v: "economics", label: "Economics", icon: CurrencyDollar },
  { v: "security", label: "Security Infrastructure", icon: Stack },
  { v: "services", label: "Services", icon: Briefcase },
  { v: "library", label: "Library", icon: FolderOpen },
  { v: "knowledge", label: "Knowledge", icon: Brain },
  { v: "content", label: "Content", icon: BookOpen },
  { v: "documents", label: "Documents", icon: FileText },
  { v: "activity", label: "Activity", icon: ClockCounterClockwise },
  { v: "history", label: "History", icon: ClockCounterClockwise },
  { v: "team", label: "Team", icon: UsersThree },
  { v: "audit", label: "Audit Trail", icon: Scroll },
  { v: "outputs", label: "Deliverables", icon: Robot },
  { v: "review", label: "Review", icon: Tray },
  { v: "connected-apps", label: "Connected Apps", icon: PlugsConnected },
  { v: "assurance", label: "Continuous Assurance", icon: Broadcast },
  { v: "radar", label: "Threat Radar", icon: Pulse },
];
export const TAB_MAP = Object.fromEntries(TABS.map((t) => [t.v, t]));

// Guided client-lifecycle flow — 5 sequential stages. De-duplicated: Library merges
// Knowledge+Documents+Content; AI Outputs merges Outputs+Review; Activity merges History+Audit;
// Services is folded into Overview.
export const LIFECYCLE_STAGES = [
  { key: "identify", num: "01", label: "Identify & Profile", icon: IdentificationCard,
    blurb: "Who is this client? Capture identity, intelligence and environment.",
    ids: ["overview", "posture", "discovery", "live-assurance", "radar"] },
  { key: "strategy", num: "02", label: "Strategy & AI Twin", icon: Brain,
    blurb: "Synthesise the digital twin and shape the engagement strategy.",
    ids: ["understanding", "engagement", "compliance", "program"] },
  { key: "grc", num: "03", label: "GRC Execution", icon: ShieldCheck,
    blurb: "Assess, score risk, remediate, evidence and audit.",
    ids: ["grc-dashboard", "assessments", "readiness", "risks", "tasks", "evidence", "policies", "audits", "audit-requests"] },
  { key: "delivery", num: "04", label: "Delivery & Growth", icon: Heartbeat,
    blurb: "Review AI work, deliver to the client and grow the relationship.",
    ids: ["outputs", "client-success", "economics", "library"] },
  { key: "operate", num: "05", label: "System & Team", icon: Gear,
    blurb: "Run the digital workforce, coordinate the team and track activity.",
    ids: ["agents", "team", "activity"] },
];

// Backward-compat / merged tab ids → the canonical firm pill that represents them.
export const FIRM_PILL_ALIAS = {
  services: "overview",
  knowledge: "library", content: "library", documents: "library",
  review: "outputs",
  history: "activity", audit: "activity",
  // Phase-2 stage consolidation (firm-side merges; client tabs unaffected).
  intelligence: "discovery", insight360: "discovery", ask: "discovery",
  security: "live-assurance", "connected-apps": "live-assurance", assurance: "live-assurance",
  requirements: "understanding",
  scoping: "engagement",
  "engagement-plan": "program",
  "sign-offs": "outputs",
  "compliance-program": "client-success",
};

// ── Stage landing hubs (hub-of-cards) ──────────────────────────────────────────
// Each lifecycle stage has a landing pseudo-tab (`stage-<key>`) that renders a bento
// grid of cards (one per sub-tab). Clicking a card deep-links to that sub-tab.
export const stageLandingId = (key) => `stage-${key}`;
export const STAGE_LANDING_IDS = LIFECYCLE_STAGES.map((s) => stageLandingId(s.key));
export const STAGE_BY_LANDING_ID = Object.fromEntries(LIFECYCLE_STAGES.map((s) => [stageLandingId(s.key), s]));
export const isStageLanding = (tab) => !!STAGE_BY_LANDING_ID[tab];

// Concise card descriptions for the stage landing hubs (one line each).
export const TAB_DESC = {
  overview: "Client identity, profile and services at a glance.",
  discovery: "AI-enriched intelligence and the 360° insight deep-scan.",
  "live-assurance": "Assurance connectors, connected apps and the security stack.",
  posture: "Compliance posture score, trend and value/ROI ledger.",
  intelligence: "AI-enriched company intelligence and findings.",
  insight360: "AI deep-crawl — completeness audit, live signals, discovery plan.",
  security: "Security infrastructure and tooling inventory.",
  "connected-apps": "Connected cloud/SaaS apps for live assurance.",
  assurance: "Continuous-assurance connectors and control drift.",
  radar: "Threat and regulatory signals impacting this client.",
  understanding: "The client digital twin plus captured requirements.",
  requirements: "Captured client requirements and objectives.",
  engagement: "Scope, parameters and scoping questionnaires.",
  scoping: "Scoping questionnaire and service shaping.",
  compliance: "Framework and regulation mapping for this client.",
  program: "The cyber program plan and engagement workstreams.",
  "engagement-plan": "Deliverables, milestones and workstreams.",
  "grc-dashboard": "GRC posture, controls and findings overview.",
  assessments: "Run and review GRC assessments.",
  readiness: "Indicative readiness across 261 frameworks.",
  risks: "Risk register — identify, score and treat risks.",
  tasks: "Remediation tasks and their progress.",
  evidence: "Evidence collection and control mapping.",
  policies: "Client policies and document control.",
  audits: "Audit engagements and their findings.",
  "audit-requests": "Client audit-evidence requests and responses.",
  "client-success": "Relationship health, growth and the client compliance program.",
  "compliance-program": "The client-facing compliance program.",
  outputs: "AI deliverables, review queue and client sign-offs.",
  "sign-offs": "Deliverable sign-offs and e-signatures.",
  economics: "Engagement economics, ARR and margin.",
  library: "Client documents, notes and reusable content.",
  agents: "Digital workforce — the engagement AI agents.",
  team: "Workspace team members and roles.",
  activity: "Engagement timeline and audit trail.",
};

export const STAGE_INDEX_OF = (pill) => {
  if (STAGE_BY_LANDING_ID[pill]) return LIFECYCLE_STAGES.indexOf(STAGE_BY_LANDING_ID[pill]);
  const i = LIFECYCLE_STAGES.findIndex((s) => s.ids.includes(pill));
  return i < 0 ? 0 : i;
};

// Resolve any tab id (incl. merged/backward-compat) to the canonical firm pill.
export const resolvePill = (tab) => FIRM_PILL_ALIAS[tab] || tab;

// Tabs an invited client-portal user may see.
export const CLIENT_TAB_IDS = ["overview", "posture", "scoping", "sign-offs", "audit-requests", "outputs", "documents"];

// Firm-side deep-link to a client workspace. Prefers the readable slug (/clients/<slug>)
// and falls back to the canonical id (/workspaces/<id>) for any workspace not yet slugged.
export function workspacePath(ws, tab) {
  const base = ws?.slug ? `/clients/${ws.slug}` : `/workspaces/${ws?.id}`;
  return tab ? `${base}?tab=${tab}` : base;
}

// Same deep-link, built from just a workspace id + a {id: slug} map (see useWorkspaceSlugIndex).
// Falls back to the canonical /workspaces/<id> until the slug map resolves — always navigable.
export function pathForWorkspace(slugIndex, id, tab) {
  const slug = slugIndex?.[id];
  const base = slug ? `/clients/${slug}` : `/workspaces/${id}`;
  return tab ? `${base}?tab=${tab}` : base;
}

// Workspace key (UUID or slug) from a firm-side workspace URL — matches /workspaces/<id>
// and /clients/<slug>. Returns null for any other path (e.g. /portal is resolved elsewhere).
export function workspaceKeyFromPath(pathname) {
  const m = pathname.match(/^\/(?:workspaces|clients)\/([^/?]+)/);
  return m ? m[1] : null;
}

// True when the current path is a firm-side workspace detail page (id or slug form).
export function isWorkspacePath(pathname) {
  return /^\/(?:workspaces|clients)\/[^/?]+/.test(pathname);
}
